/**
 * useTopicBacklog — real-time subscription to projects/{pid}/topic_backlog/.
 * Used by the new Topic Backlog sub-tab.
 */

import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ContentTopic } from '../types';

function createdAtMillis(topic: ContentTopic): number {
  const value = (topic as { createdAt?: unknown }).createdAt;
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (value instanceof Date) return value.getTime();
  return 0;
}

function sortNewestFirst(topics: ContentTopic[]): ContentTopic[] {
  return [...topics].sort((a, b) => createdAtMillis(b) - createdAtMillis(a));
}

export interface UseTopicBacklogReturn {
  topics: ContentTopic[];
  loading: boolean;
  error: Error | null;
}

export function useTopicBacklog(projectId: string | null, authorId?: string | null): UseTopicBacklogReturn {
  const [topics, setTopics] = useState<ContentTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || authorId === 'all') {
      setTopics([]); setLoading(false); return;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const base = collection(db, 'projects', projectId, 'topic_backlog');
    const q = authorId
      ? query(base, where('authorId', '==', authorId))
      : query(base, orderBy('createdAt', 'desc'));
    setLoading(true);
    const unsub = onSnapshot(q,
      (snap) => {
        const list = snap.docs.map((d) => d.data() as ContentTopic);
        setTopics(authorId ? sortNewestFirst(list) : list);
        setLoading(false);
      },
      (err) => { setError(err as Error); setLoading(false); },
    );
    return () => unsub();
  }, [projectId, authorId]);

  return { topics, loading, error };
}
